// La vie de propriétaire : la chaudière, la copropriété, la taxe foncière, le crédit et son assurance, les travaux.
import type { GameState, StageId } from '../../engine/state';
import { DAYS_PER_YEAR } from '../../engine/time';
import { delegationSaving, mortgageLoan, rateCutSaving } from '../../game/property';
import type { EventDef } from '../events';
import { between, simpleEvent } from './kit';

const OWNER_STAGES: StageId[] = ['stage1', 'stage2'];
/** La baisse de taux d'une renégociation : 0,6 point. */
const RATE_CUT = 0.006;

const owner = (state: GameState) => state.housing === 'proprio' && state.home !== null;
/** Un appartement : la copropriété, et le voisin du dessus. */
const flat = (state: GameState) => owner(state) && state.home?.kind !== 'maison';
/** Les années depuis l'achat. */
const yearsOwned = (state: GameState) => (state.home ? (state.day - state.home.boughtDay) / DAYS_PER_YEAR : 0);

export const EVENTS: EventDef[] = [
  simpleEvent({
    id: 'chaudiere_panne',
    kind: 'imprevu',
    stages: OWNER_STAGES,
    bad: true,
    shock: 6,
    maxCount: 2,
    cooldownDays: 1500,
    condition: owner,
    params: (state) => ({
      cost: between(state, 2500, 4000),
      repair: between(state, 400, 700),
      extra: Math.round(15 * state.priceLevel),
    }),
    choices: [
      { id: 'remplacer', effects: (_, p) => ({ cash: -p.cost }) },
      { id: 'livret', effects: (_, p) => ({ livret: -p.cost }) },
      { id: 'reparer', effects: (_, p) => ({ cash: -p.repair, addExpense: { id: 'energie', amount: p.extra } }) },
    ],
  }),
  simpleEvent({
    id: 'ravalement_facade',
    kind: 'imprevu',
    stages: OWNER_STAGES,
    bad: true,
    shock: 5,
    maxCount: 1,
    condition: flat,
    params: (state) => ({ cost: between(state, 3000, 5000) }),
    choices: [
      { id: 'payer', effects: (_, p) => ({ cash: -p.cost }) },
      { id: 'livret', effects: (_, p) => ({ livret: -p.cost }) },
      { id: 'echelonner', effects: (_, p) => ({ loan: { amount: p.cost, rate: 0, months: 12 } }) },
    ],
  }),
  simpleEvent({
    id: 'taxe_fonciere_hausse',
    kind: 'imprevu',
    stages: OWNER_STAGES,
    maxCount: 3,
    cooldownDays: 730,
    condition: owner,
    params: (state) => {
      const hausse = Math.max(3, Math.round((state.expenses.taxeFonciere?.amount ?? 80) * 0.08));
      return { hausse, demi: Math.round(hausse / 2) };
    },
    choices: [
      { id: 'accepter', effects: (_, p) => ({ addExpense: { id: 'taxeFonciere', amount: p.hausse } }) },
      {
        id: 'verifier',
        effects: (_, p) => ({ addExpense: { id: 'taxeFonciere', amount: p.demi }, energy: -3, knowledge: 1 }),
      },
    ],
  }),
  simpleEvent({
    // La loi permet de changer d'assurance emprunteur à tout moment, pour des garanties équivalentes.
    id: 'assurance_emprunteur_changer',
    kind: 'quotidien',
    stages: OWNER_STAGES,
    maxCount: 1,
    condition: (state) =>
      owner(state) && mortgageLoan(state) !== undefined && state.home?.insurance === 'banque' && yearsOwned(state) >= 1,
    params: (state) => ({
      current: Math.round(mortgageLoan(state)?.insuranceMonthly ?? 50),
      saving: Math.max(1, Math.round(delegationSaving(state) || 30)),
    }),
    choices: [
      { id: 'changer', effects: { mortgageInsurance: 'deleguee', energy: -2, page: 'assurance_emprunteur' } },
      { id: 'garder' },
    ],
  }),
  simpleEvent({
    // Une autre banque rachète le crédit à un meilleur taux : les frais sont vite remboursés sur la durée.
    id: 'renegociation_credit',
    kind: 'chance',
    stages: OWNER_STAGES,
    maxCount: 1,
    condition: (state) => {
      const loan = mortgageLoan(state);
      return owner(state) && loan !== undefined && loan.monthsLeft >= 96 && loan.annualRate >= 0.025 && yearsOwned(state) >= 2;
    },
    params: (state) => ({
      fees: between(state, 800, 1200),
      saving: Math.max(1, Math.round(rateCutSaving(state, RATE_CUT) || 40)),
    }),
    choices: [
      { id: 'renegocier', effects: (_, p) => ({ cash: -p.fees, mortgageRateCut: RATE_CUT }) },
      { id: 'garder' },
    ],
  }),
  simpleEvent({
    id: 'isolation_aides',
    kind: 'quotidien',
    stages: OWNER_STAGES,
    maxCount: 1,
    condition: (state) => owner(state) && (state.expenses.energie?.amount ?? 0) >= 50,
    params: (state) => {
      const cost = between(state, 9000, 11000);
      const aide = between(state, 3000, 4000);
      return { cost, aide, net: cost - aide, saving: Math.round((state.expenses.energie?.amount ?? 80) * 0.3) };
    },
    choices: [
      { id: 'isoler', effects: (_, p) => ({ cash: -p.net, addExpense: { id: 'energie', amount: -p.saving }, wellbeing: 2 }) },
      { id: 'livret', effects: (_, p) => ({ livret: -p.net, addExpense: { id: 'energie', amount: -p.saving }, wellbeing: 2 }) },
      { id: 'plus_tard' },
    ],
  }),
];
